import {Dialog, MultiChoice} from 'platform/components';
import {VStack} from 'platform/foundation';

import {useState} from 'react';

import {useBulkUpdateOpeningHoursMutation} from '@dms/api/base';
import i18n from '@dms/i18n';
import {testIds} from '@dms/routes';
import {ALL_BRANCHES_VALUE, handleApiError, useBranches} from '@dms/shared';

import {
  DaySlots,
  OpeningHoursCategory,
  normalizeDaySlotsToHHMMSS,
} from '../utils/openingHoursUtils';

const allBranchesOption = [
  {value: ALL_BRANCHES_VALUE, label: i18n.t('entity.branch.labels.allBranches')},
];

interface CopyToOtherBranchesDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirmSuccess?: () => void;
  currentBranchId: string;
  activeCategory: OpeningHoursCategory;
  openingHoursData: DaySlots;
}

export function CopyToOtherBranchesDialog(props: CopyToOtherBranchesDialogProps) {
  const [selectedBranchIds, setSelectedBranchIds] = useState<string[]>([]);
  const {branchOptions} = useBranches();
  const [bulkUpdateOpeningHours, {isLoading}] = useBulkUpdateOpeningHoursMutation();

  const filteredOptions = branchOptions.filter((option) => option.value !== props.currentBranchId);
  const isAllBranchesSelected = selectedBranchIds.includes(ALL_BRANCHES_VALUE);
  const options = isAllBranchesSelected
    ? allBranchesOption
    : [...allBranchesOption, ...filteredOptions];

  const branchIdsToUse = isAllBranchesSelected
    ? [props.currentBranchId, ...filteredOptions.map((option) => option.value)]
    : [props.currentBranchId, ...selectedBranchIds];

  const handleSelect = (value: string[] | null) => {
    setSelectedBranchIds(value ?? []);
  };

  const handleConfirm = () => {
    bulkUpdateOpeningHours({
      branchIds: branchIdsToUse,
      [props.activeCategory]: normalizeDaySlotsToHHMMSS(props.openingHoursData),
    })
      .unwrap()
      .then(() => {
        props.onConfirmSuccess?.();
        props.onClose();
      })
      .catch(handleApiError);
  };

  return (
    <Dialog
      isOpen={props.isOpen}
      onClose={props.onClose}
      size="small"
      scrollBehavior="outside"
      title={i18n.t('entity.branch.labels.copyToOtherBranches')}
      buttons={[
        {
          variant: 'secondary',
          onClick: props.onClose,
          title: i18n.t('general.actions.discard'),
          'data-testid': testIds.settings.branches('copyToOtherBranchesDialog-discard'),
        },
        {
          variant: 'primary',
          onClick: handleConfirm,
          title: i18n.t('general.actions.confirm'),
          isLoading,
          isDisabled: !isAllBranchesSelected && selectedBranchIds.length === 0,
          'data-testid': testIds.settings.branches('copyToOtherBranchesDialog-confirm'),
        },
      ]}
      data-testid={testIds.settings.branches('copyToOtherBranchesDialog')}
    >
      <VStack spacing={4}>
        <MultiChoice
          value={selectedBranchIds}
          options={options}
          onChange={handleSelect}
          hasOptionCheckbox
          placeholder={i18n.t('entity.branch.labels.selectBranches')}
          closeMenuOnSelect={false}
          data-testid={testIds.settings.branches('copyToOtherBranchesDialog-branchSelect')}
        />
      </VStack>
    </Dialog>
  );
}
